import { format, isToday, isYesterday, isThisYear, formatDuration, differenceInCalendarDays } from 'date-fns';
import { TFunction } from 'i18next';
import { getLocale } from '@/components/common/translateFunctions';

const DEFAULT_DATE_FORMATS = {
  HM: 'HH:mm',
  MD: 'MM. dd.',
  MDE: 'MM. dd. (E)',
  MDEHM: 'MMM dd. (E), HH:mm',
  MDEHMS: 'MM. dd. (EEE), HH:mm:ss',
  YM: 'yyyy MMMM',
  YMD: 'yyyy. MM. dd.',
  YMDE: 'yyyy. MM. dd. (E)',
  YMDHM: 'yyyy. MM. dd. HH:mm',
  YMDHMS: 'yyyy. MM. dd. HH:mm:ss',
} as const;

function getDateFormat(formatName: keyof typeof DEFAULT_DATE_FORMATS, translateFunctions: TFunction): string {
  const key = `general.dateFormat.${formatName}`;
  const fallback = DEFAULT_DATE_FORMATS[formatName];
  const translatedFormat = translateFunctions(key, {
    defaultValue: fallback,
  });
  if (typeof translatedFormat !== 'string' || translatedFormat.trim() === '' || translatedFormat === key) {
    return fallback;
  }
  return translatedFormat;
}

export function dateYMD(date: Date): string {
  return format(date, 'yyyyMMdd');
}

export function date(date: Date, translateFunctions: TFunction): string {
  const patternYMD = getDateFormat('YMD', translateFunctions);
  return format(date, patternYMD);
}

export function isValidDate(date: Date): boolean {
  return !isNaN(date.getTime());
}

export function dateDot(dateFrom: Date, dateTo: Date, translateFunctions: TFunction): string {
  const patternYMD = getDateFormat('YMD', translateFunctions);
  if (dateFrom.getFullYear() === dateTo.getFullYear()) {
    if (dateFrom.getMonth() === dateTo.getMonth() && dateFrom.getDate() === dateTo.getDate()) {
      return format(dateFrom, patternYMD);
    }
    const patternMD = getDateFormat('MD', translateFunctions);
    return format(dateFrom, patternYMD) + '-' + format(dateTo, patternMD);
  } else return format(dateFrom, patternYMD) + '-' + format(dateTo, patternYMD);
}

export function datePeriodMD(dateFrom: Date, dateTo: Date, translateFunctions: TFunction): string {
  const patternYMD = getDateFormat('YMD', translateFunctions);
  const patternMD = getDateFormat('MD', translateFunctions);

  return format(dateFrom, patternYMD) + ' - ' + format(dateTo, patternMD);
}

export function dateDistance(from: Date, to: Date, language: string): string {
  const days = differenceInCalendarDays(from, to) + 1;
  const locale = getLocale(language);
  return formatDuration({ days }, { locale });
}

export function fulldate(timeMicros: number, translateFunctions: TFunction): string {
  const patternYMDHMS = getDateFormat('YMDHMS', translateFunctions);
  return format(new Date(timeMicros / 1000), patternYMDHMS);
}

export function fullDateNoYearNoSeconds(
  timeMicros: number,
  currentLanguage: string,
  translateFunctions: TFunction
): string {
  const patternMDEHM = getDateFormat('MDEHM', translateFunctions);
  return format(new Date(timeMicros / 1000), patternMDEHM, { locale: getLocale(currentLanguage) });
}

export function fullDateNoYear(timeMicros: number, currentLanguage: string, translateFunctions: TFunction): string {
  const patternMDEHMS = getDateFormat('MDEHMS', translateFunctions);
  return format(new Date(timeMicros / 1000), patternMDEHMS, { locale: getLocale(currentLanguage) });
}

export function fullDateNoSecond(timeMicros: number, translateFunctions: TFunction): string {
  if (isNaN(timeMicros)) {
    console.log('fullDateNoSecond received NaN');
    return '';
  }
  const patternYMDHM = getDateFormat('YMDHM', translateFunctions);
  return format(new Date(timeMicros / 1000), patternYMDHM);
}

export function chatdate(timeMicros: number, currentLanguage: string, translateFunctions: TFunction): string {
  const date = new Date(timeMicros / 1000);
  const patternYMDHM = getDateFormat('YMDHM', translateFunctions);
  const patternHM = getDateFormat('HM', translateFunctions);
  const patternMDEHM = getDateFormat('MDEHM', translateFunctions);

  let relTime;
  if (isToday(date)) {
    relTime = translateFunctions('general.dateTime.today') + ', ' + format(date, patternHM);
  } else if (isYesterday(date)) {
    relTime = translateFunctions('general.dateTime.yesterday') + ', ' + format(date, patternHM);
  } else if (isThisYear(date)) {
    relTime = format(date, patternMDEHM, { locale: getLocale(currentLanguage) });
  } else {
    relTime = format(date, patternYMDHM);
  }

  return relTime;
}

export function dateYMDE(date: Date, currentLanguage: string, translateFunctions: TFunction): string {
  const patternYMDE = getDateFormat('YMDE', translateFunctions);
  return format(date, patternYMDE, { locale: getLocale(currentLanguage) });
}

export function dateMDE(date: Date, currentLanguage: string, translateFunctions: TFunction): string {
  if (!date) return '';
  const patternMDE = getDateFormat('MDE', translateFunctions);
  return format(date, patternMDE, { locale: getLocale(currentLanguage) });
}

export function datePicker(date: Date, locale: Locale, translateFunctions: TFunction): string {
  const patternYM = getDateFormat('YM', translateFunctions);
  return format(date, patternYM, { locale });
}
